import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfiguration } from '../../context/ConfigurationContext';
import {
  STRUGGLE_OPTIONS,
  STRUGGLES_QUESTION,
  determineProfileFromStruggles,
  type Struggle,
} from '../../constants/pre-screening-questions';

interface StrugglesProps {
  onNext: () => void;
  onBack: () => void;
}

const Struggles: FC<StrugglesProps> = ({ onNext, onBack }) => {
  const { i18n } = useTranslation();
  const { setSelectedProfile, completeAssessment } = useConfiguration();
  const [selected, setSelected] = useState<Struggle[]>([]);
  const isFr = i18n.language?.startsWith('fr');

  const toggleStruggle = (value: Struggle) => {
    setSelected(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    );
  };

  const handleContinue = () => {
    const { profile, severity } = determineProfileFromStruggles(selected);
    setSelectedProfile(profile);

    // Map severity to a score for completeAssessment
    const scoreMap = { mild: 4, moderate: 8, severe: 12 };
    completeAssessment([], severity, scoreMap[severity]);

    onNext();
  };

  return (
    <div className="min-h-screen bg-brand-bg dark:bg-slate-900 font-body pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-brand-border dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-brand-text-light hover:text-brand-text dark:text-slate-400 dark:hover:text-white transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="font-medium">{isFr ? 'Retour' : 'Back'}</span>
            </button>
            <span className="font-bold text-brand-text dark:text-white">{STRUGGLES_QUESTION.code}</span>
            <div className="w-20" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-primary/10 text-brand-primary font-bold text-lg mb-4">
            Q5
          </div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-brand-text dark:text-white">
            {isFr ? STRUGGLES_QUESTION.textFr : STRUGGLES_QUESTION.text}
          </h2>
          <p className="text-brand-text-light dark:text-slate-400 mt-2">
            {isFr ? STRUGGLES_QUESTION.subtitleFr : STRUGGLES_QUESTION.subtitle}
          </p>
        </div>

        <div className="grid gap-3">
          {STRUGGLE_OPTIONS.map((option) => {
            const isSelected = selected.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => toggleStruggle(option.value)}
                className={`group w-full text-left p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 ${
                  isSelected
                    ? 'border-brand-primary bg-brand-primary/5 shadow-md'
                    : 'border-brand-border dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-brand-primary/50 hover:bg-brand-primary/5'
                }`}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  {/* Checkbox */}
                  <div
                    className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      isSelected
                        ? 'border-brand-primary bg-brand-primary'
                        : 'border-gray-300 dark:border-slate-600'
                    }`}
                  >
                    {isSelected && (
                      <span className="material-symbols-outlined text-white" style={{ fontSize: '16px' }}>check</span>
                    )}
                  </div>

                  {/* Icon */}
                  <span className={`material-symbols-outlined ${isSelected ? 'text-brand-primary' : 'text-gray-400'}`} style={{ fontSize: '24px' }}>
                    {option.icon}
                  </span>

                  {/* Label */}
                  <span className={`text-base font-medium transition-colors ${
                    isSelected ? 'text-brand-primary' : 'text-brand-text dark:text-white'
                  }`}>
                    {isFr ? option.labelFr : option.label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-brand-border dark:border-slate-800 p-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            onClick={onBack}
            className="px-6 py-3 text-brand-text-light dark:text-slate-400 font-medium hover:text-brand-text dark:hover:text-white transition-colors"
          >
            {isFr ? 'Retour' : 'Back'}
          </button>

          <div className="text-sm text-brand-text-muted">
            {selected.length > 0
              ? `${selected.length} ${isFr ? 'sélectionné(s)' : 'selected'}`
              : isFr ? 'Sélectionnez au moins un' : 'Select at least one'}
          </div>

          <button
            onClick={handleContinue}
            disabled={selected.length === 0}
            className="px-6 py-3 bg-brand-primary text-white font-semibold rounded-xl hover:bg-brand-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isFr ? 'Suivant' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Struggles;
